import {
	FloatingFocusManager,
	FloatingPortal,
	autoUpdate,
	flip,
	offset,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useRole,
} from "@floating-ui/react";
import React, { useState } from "react";

import { faCaretDown } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonIcon, Spinner, Text } from "@performyard/py-components";

import Avatar from "../../../../../components/Avatar";

import { useMeetingContext } from "../Meetings/MeetingContext";

interface PublicNotesHeaderProps {
	showMyPublicNotes: boolean;
	showPublicNotesSaved: boolean;
	showPublicNotesSaving: boolean;
	onOptionSelect: (option: { id: string; label: string }) => void;
	isPublicNotesError: boolean;
}

const PublicNotesHeader = ({
	showMyPublicNotes,
	showPublicNotesSaved,
	showPublicNotesSaving,
	onOptionSelect,
	isPublicNotesError,
}: PublicNotesHeaderProps) => {
	const { userId, participantId, participantName, meeting, workspace, isAdminView } = useMeetingContext();
	const [isOpen, setIsOpen] = useState(false);

	// Get meeting creator name for admin view (first participant or current user)
	const meetingCreatorId = meeting?.created?.userId || userId;
	const meetingCreatorName =
		workspace?.participants?.find((p) => p.userId === meetingCreatorId)?.name || "Meeting Owner";

	// Determine labels based on admin view
	const myNotesLabel = isAdminView ? `${meetingCreatorName}'s Shared Notes` : "My Shared Notes";
	const participantNotesLabel = `${participantName || "Participant"}'s Shared Notes`;

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [offset(5), flip({ fallbackAxisSideDirection: "end" }), shift()],
		whileElementsMounted: autoUpdate,
		placement: "bottom-start",
	});

	const click = useClick(context);
	const dismiss = useDismiss(context);
	const role = useRole(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

	const handleSelect = (option: { id: string; label: string }) => {
		onOptionSelect(option);
		setIsOpen(false);
	};

	return (
		<div className="relative">
			<div className="flex items-center">
				<div
					className="flex items-center space-x-1 focus:outline-none focus:ring-0"
					ref={refs.setReference}
					{...getReferenceProps()}
				>
					<Avatar
						size={20}
						id={showMyPublicNotes ? (isAdminView ? meetingCreatorId : userId) : participantId || ""}
					/>
					<Text
						size="paragraph-3"
						weight="semi-bold"
						dangerouslySetClassName="hover:cursor-pointer hover:underline"
					>
						{showMyPublicNotes ? myNotesLabel : participantNotesLabel}
					</Text>
				</div>
				<ButtonIcon icon={faCaretDown} size={16} onClick={() => setIsOpen((prev) => !prev)} />
				<div className="ml-auto flex h-[20px] min-w-[60px] items-center justify-end overflow-hidden">
					{showMyPublicNotes && showPublicNotesSaving && !isPublicNotesError && (
						<div className="flex items-center space-x-2 text-[10px] text-neutral-9">
							<Text size="extra-small" weight="regular">
								Saving
							</Text>
							<Spinner size={12} />
						</div>
					)}
					{showMyPublicNotes && showPublicNotesSaved && !isPublicNotesError && (
						<div className="flex items-center text-[10px] text-neutral-9">
							<Text size="extra-small" weight="regular">
								Saved
							</Text>
						</div>
					)}
				</div>
			</div>

			{isOpen && (
				<FloatingPortal>
					<FloatingFocusManager context={context} modal={false}>
						<div
							ref={refs.setFloating}
							style={{ ...floatingStyles, zIndex: 2010 }}
							{...getFloatingProps()}
							className="rounded-lg border-[1px] border-solid border-neutral-3 bg-white"
							tabIndex={-1}
						>
							<button
								className="h-8 w-full rounded-t-lg border-none bg-white px-2 text-left hover:cursor-pointer hover:bg-neutral-2 focus:outline-none focus:ring-0"
								onClick={() => handleSelect({ id: "my-notes", label: "My Notes" })}
							>
								<div className="flex items-center space-x-1">
									<Avatar id={isAdminView ? meetingCreatorId : userId} size={20} />
									<Text size="paragraph-3" weight="regular" color="neutral-7">
										{myNotesLabel}
									</Text>
								</div>
							</button>
							<button
								className="h-8 w-full rounded-b-lg border-none bg-white px-2 text-left hover:cursor-pointer hover:bg-neutral-2 focus:outline-none focus:ring-0"
								onClick={() => handleSelect({ id: "participant-notes", label: "Participant's Notes" })}
							>
								<div className="flex items-center space-x-1">
									<Avatar id={participantId || ""} size={20} />
									<Text size="paragraph-3" weight="regular" color="neutral-7">
										{participantNotesLabel}
									</Text>
								</div>
							</button>
						</div>
					</FloatingFocusManager>
				</FloatingPortal>
			)}
		</div>
	);
};

export default PublicNotesHeader;
